import type { FormEvent } from 'react';

import { CalendarDays, Clock3, HandHeart, Loader2, MapPin, Repeat2, Users } from 'lucide-react';
import { type Control, Controller } from 'react-hook-form';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { CurrencyInputField } from '@nesso/ui/primitives/currency-input-field';
import { DialogFooter, DialogHeader, DialogTitle } from '@nesso/ui/primitives/dialog';
import { Field, FieldLabel } from '@nesso/ui/primitives/field';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@nesso/ui/primitives/select';

import type {
    ScheduleSessionFormOutput,
    ScheduleSessionFormValues,
} from '../../model/schedule-session-form.schema';
import { ScheduleSessionClientPicker } from './schedule-session-form/schedule-session-client-picker';
import { ScheduleSessionDatePicker } from './schedule-session-form/schedule-session-date-picker';
import { ScheduleSessionLocationFields } from './schedule-session-form/schedule-session-location-fields';
import { ScheduleSessionServicePicker } from './schedule-session-form/schedule-session-service-picker';
import { ScheduleSessionTimeFields } from './schedule-session-form/schedule-session-time-fields';

type Props = {
    control: Control<ScheduleSessionFormValues, unknown, ScheduleSessionFormOutput>;
    isPending: boolean;
    isXs: boolean;
    onServiceChange: (code: string) => void;
    onStartTimeChange: (value: string) => void;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
    onCancel: () => void;
};

// `isXs`: the fullscreen dialog carries no padding of its own and would stack its footer
// full-width per `DialogFooter`'s `sm:` breakpoint — both compensated here, matching
// `nesso-add-approach-dialog.tsx`.
export function ScheduleSessionForm({
    control,
    isPending,
    isXs,
    onServiceChange,
    onStartTimeChange,
    onSubmit,
    onCancel,
}: Props) {
    return (
        <div className={cn('flex min-w-0 flex-col', isXs ? 'min-h-0 flex-1' : 'gap-4 px-6 pb-6')}>
            <DialogHeader className={cn('text-left', isXs && 'px-6 pb-4')}>
                <DialogTitle className="text-xl leading-7 font-medium">New Session</DialogTitle>
            </DialogHeader>
            <form
                onSubmit={onSubmit}
                className={cn('flex min-w-0 flex-col', isXs ? 'min-h-0 flex-1 gap-6' : 'gap-7')}
            >
                <div className={cn('flex flex-col gap-6', isXs && 'flex-1 overflow-y-auto px-6')}>
                    {/* display:contents keeps this container's own flex gap while still letting one
                        disabled fieldset switch off every row's picker, input and radio together. */}
                    <fieldset disabled={isPending} className="contents">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 shrink-0 items-center">
                                <Users className="size-4 text-muted-foreground" />
                            </div>
                            <Controller
                                control={control}
                                name="client"
                                render={({ field, fieldState }) => (
                                    <ScheduleSessionClientPicker
                                        value={field.value}
                                        error={fieldState.error?.message ?? fieldState.invalid}
                                        onSelect={field.onChange}
                                    />
                                )}
                            />
                        </div>

                        <div className="flex flex-col gap-3">
                            <div className="flex items-start gap-4">
                                <div className="flex h-10 shrink-0 items-center">
                                    <CalendarDays className="size-4 text-muted-foreground" />
                                </div>
                                <Controller
                                    control={control}
                                    name="date"
                                    render={({ field, fieldState }) => (
                                        <ScheduleSessionDatePicker
                                            value={field.value}
                                            error={fieldState.error?.message ?? fieldState.invalid}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="flex h-10 shrink-0 items-center">
                                    <Clock3 className="size-4 text-muted-foreground" />
                                </div>
                                <ScheduleSessionTimeFields
                                    control={control}
                                    onStartTimeChange={onStartTimeChange}
                                />
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="flex h-10 shrink-0 items-center">
                                    <Repeat2 className="size-4 text-muted-foreground" />
                                </div>
                                <ScheduleSessionRepeatField />
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            {/* h-8 is the radio row's height; the other rows' h-10 is an input's. */}
                            <div className="flex h-8 shrink-0 items-center">
                                <MapPin className="size-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1">
                                <ScheduleSessionLocationFields control={control} />
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="flex h-10 shrink-0 items-center">
                                <HandHeart className="size-4 text-muted-foreground" />
                            </div>
                            <div className="flex w-full min-w-0">
                                <Controller
                                    control={control}
                                    name="serviceCode"
                                    render={({ field, fieldState }) => (
                                        <ScheduleSessionServicePicker
                                            value={field.value}
                                            error={fieldState.error?.message ?? fieldState.invalid}
                                            onSelect={onServiceChange}
                                            triggerClassName="rounded-r-none"
                                            fieldClassName="min-w-0 flex-1"
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="fee"
                                    render={({ field, fieldState }) => (
                                        <CurrencyInputField
                                            {...field}
                                            id="schedule-session-fee"
                                            label="Fee"
                                            hideLabel
                                            error={fieldState.error?.message ?? fieldState.invalid}
                                            // min-w-12 fits a three-digit fee; a longer amount widens the field.
                                            className="field-sizing-content min-w-12"
                                            fieldClassName="w-fit shrink-0"
                                            inputGroupClassName="w-fit rounded-l-none border-l-0"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </fieldset>
                </div>

                <DialogFooter className={cn(isXs && 'flex-row gap-2 p-6')}>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={isPending}
                        onClick={onCancel}
                        className={cn(isXs && 'flex-1')}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isPending} className={cn(isXs && 'flex-1')}>
                        {isPending && <Loader2 className="animate-spin" />}
                        Save
                    </Button>
                </DialogFooter>
            </form>
        </div>
    );
}

// A disabled `Select` (rather than a static label) reads correctly to assistive tech; the label
// is visually hidden since the row shows only the icon and the trigger's own text.
function ScheduleSessionRepeatField() {
    const wiring = useFieldWiring({});

    return (
        // Overrides `Field`'s own `w-full`, which would stretch this to the row's full field column.
        <Field data-invalid={wiring.dataInvalid} className="w-fit">
            <FieldLabel htmlFor={wiring.controlId} className="sr-only">
                Repeat
            </FieldLabel>
            <Select value="DOES_NOT_REPEAT" disabled>
                <SelectTrigger id={wiring.controlId} className="bg-input-background w-fit">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="DOES_NOT_REPEAT">Does not repeat</SelectItem>
                </SelectContent>
            </Select>
        </Field>
    );
}
